
import React, { useState, useEffect } from 'react';
import { landingPageService } from '../services/landingPageService';
import { socialCommerceService } from '../services/socialCommerceService';
import { LandingPage, Product, Store, SocialVideo } from '../types';
import { storage } from '../lib/storage';
import { 
  Zap, 
  ArrowRight, 
  CheckCircle2, 
  Star, 
  ShieldCheck, 
  Truck, 
  Sparkles, 
  Loader2,
  ShoppingCart,
  ChevronRight,
  Play
} from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';
import VideoShoppingCard from '../components/VideoShoppingCard';

interface Props {
  slug: string;
  onAddToCart: (product: Product) => void;
  onViewProduct: (product: Product) => void;
}

const CampaignLandingPage: React.FC<Props> = ({ slug, onAddToCart, onViewProduct }) => {
  const [lp, setLp] = useState<LandingPage | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [videos, setVideos] = useState<SocialVideo[]>([]);
  const [store, setStore] = useState<Store | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { format } = useCurrency();

  useEffect(() => {
    const data = landingPageService.getLPBySlug(slug);
    if (data) {
      setLp(data);
      setStore(storage.getStoreConfig(data.storeId));
      const allProds = storage.getProducts();
      setProducts(allProds.filter(p => data.productIds.includes(p.id)));
      setVideos(socialCommerceService.getStoreVideos(data.storeId).slice(0, 3));
      landingPageService.trackView(data.id);
    }
    setIsLoading(false);
  }, [slug]);

  if (isLoading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center space-y-4">
        <Loader2 className="h-10 w-10 text-indigo-600 animate-spin" />
        <p className="text-gray-400 font-bold uppercase tracking-widest text-[10px]">Synchronizing Campaign...</p>
      </div>
    );
  }

  if (!lp) {
    return (
      <div className="h-screen flex flex-col items-center justify-center space-y-6 text-center px-4">
         <h1 className="text-4xl font-black text-gray-900 tracking-tight">Campaign Not Found</h1>
         <p className="text-gray-500 max-w-sm">Bhai, ye link expire ho chuka hai ya galat hai. Store home page check karein.</p>
         <a href="/" className="bg-blue-600 text-white px-10 py-4 rounded-2xl font-black uppercase text-xs">Back to Marketplace</a>
      </div>
    );
  }

  const handleBuy = (p: Product) => {
    onAddToCart(p);
    landingPageService.trackConversion(lp.id);
  };

  return (
    <div className="bg-white min-h-screen animate-in fade-in duration-1000">
      {/* Landing Header - Exclusive for Campaign */}
      <nav className="h-20 border-b border-gray-100 flex items-center justify-center px-8 bg-white/80 backdrop-blur-xl sticky top-0 z-50">
         <div className="flex items-center gap-2 font-black text-xl tracking-tighter">
            <span className="bg-indigo-600 text-white px-2 py-0.5 rounded">L</span>
            <span className="text-gray-900 uppercase">{store?.name}</span>
         </div>
      </nav>

      {/* Hero Section */}
      <section className="max-w-6xl mx-auto px-6 pt-20 pb-32 text-center space-y-12">
         <div className="space-y-6">
            <div className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-600 px-6 py-2 rounded-full text-[10px] font-black uppercase tracking-[0.2em]">
               <Sparkles className="h-3 w-3" /> Exclusive Campaign Offer
            </div>
            <h1 className="text-6xl md:text-8xl font-black text-gray-900 tracking-tighter leading-[0.9]">
               {lp.heroHeadline}
            </h1>
            <p className="text-xl md:text-2xl text-gray-500 font-medium max-w-3xl mx-auto leading-relaxed">
               {lp.heroSubheadline}
            </p>
         </div>
         
         <div className="flex justify-center gap-4">
            <a href="#featured-products" className="bg-indigo-600 text-white px-12 py-6 rounded-[2.5rem] font-black text-xl shadow-2xl shadow-indigo-200 hover:scale-105 active:scale-95 transition-all flex items-center gap-3">
               {lp.ctaText} <ArrowRight className="h-6 w-6" />
            </a>
         </div>
      </section>

      {/* Social Shopping Section - Shop-able Reels */}
      {videos.length > 0 && (
        <section className="py-24 bg-gray-950 overflow-hidden">
           <div className="max-w-7xl mx-auto px-6 space-y-16">
              <div className="text-center space-y-4">
                 <div className="bg-red-600 text-white px-4 py-1.5 rounded-full text-[8px] font-black uppercase tracking-widest inline-flex items-center gap-2 animate-pulse mb-2">
                    <div className="w-1.5 h-1.5 bg-white rounded-full"></div> LIVE SHOPPING
                 </div>
                 <h2 className="text-4xl font-black text-white tracking-tight">Experience it Live</h2>
                 <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">Tap to shop products from the video</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
                 {videos.map(vid => {
                   const product = products.find(p => p.id === vid.productId);
                   return product ? (
                     <VideoShoppingCard 
                       key={vid.id} 
                       video={vid} 
                       product={product} 
                       onAddToCart={onAddToCart} 
                     />
                   ) : null;
                 })}
              </div>
           </div>
        </section>
      )}

      {/* Featured Products Visual Hub */}
      <section id="featured-products" className="bg-gray-50 py-32">
         <div className="max-w-7xl mx-auto px-6 space-y-20">
            <div className="text-center space-y-4">
               <h2 className="text-4xl font-black text-gray-900 tracking-tight">The Core Collection</h2>
               <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">Hand-picked for this campaign</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
               {products.map(product => (
                 <div key={product.id} className="bg-white rounded-[50px] shadow-xl overflow-hidden group hover:-translate-y-2 transition-all duration-500">
                    <div className="aspect-square relative overflow-hidden">
                       <img src={product.images[0]} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" alt="" />
                       <div className="absolute top-6 left-6">
                          <span className="bg-white/90 backdrop-blur px-4 py-2 rounded-2xl text-[8px] font-black uppercase tracking-widest shadow-lg">New Arrival</span>
                       </div>
                    </div>
                    <div className="p-10 space-y-6">
                       <div className="flex justify-between items-start">
                          <h3 className="text-2xl font-black text-gray-900 leading-tight">{product.name}</h3>
                          <p className="text-2xl font-black text-indigo-600">{format(product.price)}</p>
                       </div>
                       <p className="text-gray-500 text-sm leading-relaxed font-medium line-clamp-2">{product.description}</p>
                       <button 
                         onClick={() => handleBuy(product)}
                         className="w-full py-5 bg-gray-900 text-white rounded-[2rem] font-black uppercase text-xs tracking-widest hover:bg-indigo-600 transition-all shadow-xl flex items-center justify-center gap-3"
                       >
                          Add to Selection <ShoppingCart className="h-4 w-4" />
                       </button>
                    </div>
                 </div>
               ))}
            </div>
         </div>
      </section>

      {/* Benefits Section */}
      <section className="py-32 max-w-7xl mx-auto px-6">
         <div className="grid grid-cols-1 md:grid-cols-3 gap-16">
            {lp.benefits.map((b, i) => (
              <div key={i} className="space-y-6 text-center">
                 <div className="w-20 h-20 bg-indigo-600 text-white rounded-[2.5rem] flex items-center justify-center mx-auto shadow-2xl shadow-indigo-100">
                    {i === 0 ? <Zap className="h-10 w-10" /> : i === 1 ? <ShieldCheck className="h-10 w-10" /> : <Truck className="h-10 w-10" />}
                 </div>
                 <h3 className="text-2xl font-black text-gray-900 tracking-tight">{b.title}</h3>
                 <p className="text-gray-500 leading-relaxed font-medium">{b.description}</p>
              </div>
            ))}
         </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-gray-950 py-20 text-white text-center">
         <div className="max-w-4xl mx-auto px-6 space-y-10">
            <div className="flex items-center justify-center gap-2 text-yellow-500">
               {[1,2,3,4,5].map(i => <Star key={i} className="h-6 w-6 fill-current" />)}
            </div>
            <p className="text-2xl md:text-3xl font-black leading-tight italic">
               "Bhai, ye deal miss nahi honi chahiye. Lynqar verified products ke saath world-class experience milta hai."
            </p>
            <div className="flex items-center justify-center gap-4 pt-4">
               <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center"><CheckCircle2 className="h-6 w-6 text-indigo-400" /></div>
               <p className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">Secure Global Checkout Active</p>
            </div>
         </div>
      </section>

      <footer className="py-20 text-center border-t border-gray-100">
         <div className="flex items-center justify-center gap-2 font-black text-xl tracking-tighter mb-6">
            <span className="bg-indigo-600 text-white px-2 py-0.5 rounded">L</span>
            <span className="text-gray-900">YNQAR</span>
         </div>
         <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Official Store Campaign • {new Date().getFullYear()}</p>
      </footer>
    </div>
  );
};

export default CampaignLandingPage;
